import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ColDef, IServerSideGetRowsRequest } from 'ag-grid-community';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ViewId } from 'src/app/api/services/view-preset-api.service';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import {
  buildAuditTrailColumnGroup,
  buildOwnerColumnDef,
} from 'src/app/ag-grid/utils/audit-trail-columns.utils';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { redirectToObject } from 'src/app/shared/utils/object-utils';
import { DataGridHostBase } from 'src/app/ag-grid/components/data-grid/data-grid-host-base';

@Component({
  selector: 'app-dashboards',
  templateUrl: './dashboards.component.html',
  standalone: false,
})
export class DashboardsComponent extends DataGridHostBase {
  private readonly userApi = inject(UserApiService);
  private readonly editService = inject(ObjectEditorService);
  private readonly router = inject(Router);

  readonly viewId = ViewId.Dashboards;
  readonly title = this.translateService.instant('grid.title.dashboards');
  readonly icon: IconProp = ['fas', 'tachometer-alt'];

  readonly fetchRows = (request: IServerSideGetRowsRequest) =>
    this.userApi.getUserDashboardsAgGrid(request);

  readonly columnDefs: ColDef[] = this.buildColumnDefs();

  private buildColumnDefs(): ColDef[] {
    return [
      {
        headerName: this.translateService.instant('grid.column.name'),
        field: 'title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          titleKey: 'title',
          objectId: 'object_id',
          objectClassId: 'object_class_id',
          objectTypeId: 'object_type_id',
          lifecycleStatus: 'lifecycle_status',
          onClick: (data) => {
            redirectToObject(data, this.router, true);
          },
        },
        cellStyle: { overflow: 'visible' },
      },
      {
        headerName: this.translateService.instant('grid.column.description'),
        field: 'description',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: this.translateService.instant(
          'grid.column.schedules-count',
        ),
        field: 'export_schedules_count',
        sortable: false,
        floatingFilter: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressHeaderContextMenu: true,
        type: 'numericColumn',
        maxWidth: 160,
      },
      {
        headerName: this.translateService.instant('grid.column.workspace'),
        field: 'workspace.title',
        filter: 'agTextColumnFilter',
        cellRenderer: AgDataObjectCellRendererComponent,
        cellRendererParams: {
          object: 'workspace',
          titleKey: 'title',
          icon: ['fas', 'folder'],
          // Only refs the dispatcher can open are links.
          clickable: (ref) => this.editService.canOpenDataObjectRef(ref),
          onClick: (ref) => this.editService.openDataObjectRef(ref),
        },
        cellStyle: { overflow: 'visible' },
      },
      buildOwnerColumnDef(this.translateService, this.openUser),
      buildAuditTrailColumnGroup(this.translateService, {
        area: 'analytics',
        onUserClick: this.openUser,
      }),
    ];
  }
}
